import type { OutreachMailboxHealthItem, OutreachMailboxLimit, OutreachMailboxStatus } from '@discovr/types';

/** Bounce and complaint rates live in fractions of a percent ("0.02%"); 2 decimals, zeros kept. */
const PRECISE_PERCENT = new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const NO_DATA = 'No data';
export const TOO_FEW_SENDS = 'Too few sends to judge';
export const POOL_NOTE = 'Nothing sent yet: showing the pool';

/** A mailbox's name: its address, or its id once it has left the pool and Go no longer has one. */
export function mailboxName(mailbox: Pick<OutreachMailboxHealthItem, 'id' | 'address'>): string {
  return mailbox.address ?? `Removed mailbox #${mailbox.id}`;
}

export function formatPrecisePercent(rate: number): string {
  return PRECISE_PERCENT.format(rate);
}

const TIGHTEST_LABEL: Record<OutreachMailboxLimit, string> = {
  sent_today: 'sent today',
  bounce: 'bounce rate',
  complaints: 'complaints',
  paused: 'paused',
};

export function tightestLimitText(tightest: OutreachMailboxLimit | null): string | null {
  return tightest === null ? null : `Tightest limit right now: ${TIGHTEST_LABEL[tightest]}`;
}

export interface MailboxLimitRow {
  key: 'sent_today' | 'bounce' | 'complaints';
  label: string;
  value: string;
  /** "of 300", "of 2%"; null when there is nothing to compare against. */
  limit: string | null;
}

const ofLimit = (limit: number | null, measured: boolean): string | null =>
  measured && limit !== null ? `of ${formatPrecisePercent(limit)}` : null;

function bounceValue(item: OutreachMailboxHealthItem): string {
  if (item.insufficientVolume) return TOO_FEW_SENDS;
  if (item.bounceRate7d === null) return NO_DATA;
  return formatPrecisePercent(item.bounceRate7d);
}

const isBounceMeasured = (item: OutreachMailboxHealthItem) => !item.insufficientVolume && item.bounceRate7d !== null;

const complaintsValue = (spamRate: number | null): string => (spamRate === null ? NO_DATA : formatPrecisePercent(spamRate));

export function mailboxLimitRows(item: OutreachMailboxHealthItem): MailboxLimitRow[] {
  return [
    { key: 'sent_today', label: 'Sent today', value: String(item.sentToday), limit: `of ${item.capToday}` },
    { key: 'bounce', label: 'Bounce', value: bounceValue(item), limit: ofLimit(item.bounceLimit, isBounceMeasured(item)) },
    {
      key: 'complaints',
      label: 'Complaints',
      value: complaintsValue(item.domainSpamRate),
      limit: ofLimit(item.spamWarn, item.domainSpamRate !== null),
    },
  ];
}

export interface MailboxStatusPill {
  label: string;
  cls: string;
}

/** A mailbox that cannot send right now says so; an active one needs no pill. */
const STATUS_PILL: Record<OutreachMailboxStatus, MailboxStatusPill | null> = {
  active: null,
  paused: { label: 'Paused', cls: 'bg-amber-100 text-(--foreground-warning-default)' },
  disabled: { label: 'Disabled', cls: 'bg-stone-200 text-stone-600' },
  resync: { label: 'Resyncing', cls: 'bg-sky-100 text-sky-700' },
  warming: { label: 'Warming up', cls: 'bg-(--brand-50) text-(--brand-600)' },
};

const UNKNOWN_STATUS_PILL: MailboxStatusPill = { label: 'Unknown', cls: 'bg-stone-200 text-stone-600' };

/** A status the page does not recognise (the mapper yields null) shows as Unknown, never as a healthy mailbox. */
export function mailboxStatusPill(status: OutreachMailboxStatus | null): MailboxStatusPill | null {
  return status === null ? UNKNOWN_STATUS_PILL : STATUS_PILL[status];
}

const FULL_PERCENT = 100;

/** The donut's two wedges: headroom left, then the used part on the track colour. */
export function headroomSegments(headroom: number | null): { pct: number; color: string }[] {
  const left = (headroom ?? 0) * FULL_PERCENT;
  return [
    { pct: left, color: 'var(--crm-headroom)' },
    { pct: FULL_PERCENT - left, color: 'var(--crm-chart-track)' },
  ];
}
